<template>
  <div class="potasium-drag__container">
    <div class="az-drag-drop__container">
      <h3 class="az-h3">Place foods in the correct basket</h3>

      <div class="baskets-container">
        <div class="basket-wrapper">
          <div
            class="basket"
            id="left-basket"
            data-basket="left"
            tabindex="0"
            aria-label="Foods to enjoy in moderation basket"
          >
            <div class="dropped-items"></div>
          </div>
          <p class="basket-label">Foods to enjoy in moderation</p>
          <div class="feedback-container__basket">
            <div class="feedback-container__basket-item success-feedback">
              <img
                src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Check_Mark.svg"
                alt="Success feedback"
                width="50"
              />
            </div>
            <div class="feedback-container__basket-item error-feedback">
              <img
                src="https://labs.theguardian.com/2025/Astra+Zeneca/error-msg.svg"
                alt="Error feedback"
                width="50"
              />
            </div>
          </div>
        </div>
        <div class="foods-container">
          <div class="foods-row">
            <div
              class="food-item"
              draggable="true"
              data-type="left"
              data-position="left"
              data-food="apple"
              tabindex="0"
              aria-label="Apple - drag to Foods to enjoy in moderation basket"
            >
              <AppleIcon />
            </div>
            <div
              class="food-item"
              draggable="true"
              data-type="right"
              data-position="left"
              data-food="carrot"
              tabindex="0"
              aria-label="Carrot - drag to Foods to enjoy in moderation basket"
            >
              <CarrotIcon />
            </div>
            <div
              class="food-item"
              draggable="true"
              data-type="right"
              data-position="left"
              data-food="banana"
              tabindex="0"
              aria-label="Banana - drag to Foods to enjoy in moderation basket"
            >
              <BananaIcon />
            </div>
          </div>
          <div class="foods-row">
            <div
              class="food-item"
              draggable="true"
              data-type="left"
              data-position="right"
              data-food="beans"
              tabindex="0"
              aria-label="Beans - drag to Foods to avoid basket"
            >
              <BeansIcon />
            </div>
            <div
              class="food-item"
              draggable="true"
              data-type="left"
              data-position="right"
              data-food="coffee"
              tabindex="0"
              aria-label="Coffee - drag to Foods to avoid basket"
            >
              <CoffieIcon />
            </div>

            <div
              class="food-item"
              draggable="true"
              data-type="right"
              data-position="right"
              data-food="chocolate"
              tabindex="0"
              aria-label="Chocolate - drag to Foods to avoid basket"
            >
              <ChocoIcon />
            </div>
          </div>
        </div>
        <div class="basket-wrapper">
          <div
            class="basket"
            id="right-basket"
            data-basket="right"
            tabindex="0"
            aria-label="Foods to avoid basket"
          >
            <div class="dropped-items"></div>
          </div>
          <p class="basket-label">Foods to avoid</p>
          <div class="feedback-container__basket">
            <div class="feedback-container__basket-item success-feedback">
              <img
                src="https://labs.theguardian.com/2025/Astra+Zeneca/assets/Check_Mark.svg"
                alt="Success feedback"
                width="50"
              />
            </div>
            <div class="feedback-container__basket-item error-feedback">
              <img
                src="https://labs.theguardian.com/2025/Astra+Zeneca/error-msg.svg"
                alt="Error feedback"
                width="50"
              />
            </div>
          </div>
        </div>
      </div>

      <button id="play-again" class="play-again-btn" style="display: none">
        Play Again
      </button>

      <div class="info-section">
        <h4 class="az-h4">{{ currentFoodInfo.title }}</h4>
        <p>
          {{ currentFoodInfo.description }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import AppleIcon from "./AppleIcon.vue";
import CarrotIcon from "./CarrotIcon.vue";
import BananaIcon from "./BananaIcon.vue";
import BeansIcon from "./BeansIcon.vue";
import CoffieIcon from "./CoffieIcon.vue";
import ChocoIcon from "./ChocoIcon.vue";

// Variables to track game state
let draggedItem = null;
let droppedItems = 0;
let demonstrationPlayed = false; // Track if demonstration has been played

// Current food info to display
const currentFoodInfo = ref({
  title: "Fruits",
  description:
    "Apples, berries (such as strawberries and blueberries), cherries, cranberries and cranberry juice, grapes, peaches, pineapple, and plums",
});

const foodData = reactive({
  apple: {
    title: "Fruits",
    description:
      "Apples, berries (such as strawberries and blueberries), cherries, cranberries and cranberry juice, grapes, peaches, pineapple, and plums",
  },
  beans: {
    title: "Vegetables",
    description:
      "Raw cabbage, cauliflower, celery, corn, cucumbers, eggplant, green beans, lettuce, onion, peppers, and zucchini",
  },
  coffee: {
    title: "Other",
    description:
      "White bread, coffee (limit to 8 ounces), noodles, white pasta, rice, and tea (limit to 16 ounces)",
  },
  carrot: {
    title: "Vegetables",
    description:
      "Artichokes, beets, broccoli (cooked), brussels sprouts, carrots,  potatoes (white and sweet), pumpkin, spinach, tomatoes, squash (acorn  and butternut), and vegetable juices",
  },
  banana: {
    title: "Fruits",
    description:
      "Apricots, avocados, bananas, cantaloupes, dried fruits, grapefruit  juice, honeydew melons, kiwis, mangos, nectarines, orange juice,  pomegranates, prunes, and raisins",
  },
  chocolate: {
    title: "Other",
    description:
      "Bran products, beans (baked beans, lentils), milk, nuts, whole wheat pasta and whole wheat bread, chocolate, and salt substitutes",
  },
});

// Function to update info section based on food type
function updateInfoSection(foodType) {
  if (foodData[foodType]) {
    currentFoodInfo.value = foodData[foodType];
  }
}

// Function to show feedback message
function showFeedback(isSuccess, basket) {
  if (!basket) return;

  // Find the feedback container for this basket
  const basketWrapper = basket.closest(".basket-wrapper");
  const feedbackContainer = basketWrapper.querySelector(
    ".feedback-container__basket"
  );

  // Select the appropriate feedback element (success or error)
  const feedbackElement = isSuccess
    ? feedbackContainer.querySelector(".success-feedback")
    : feedbackContainer.querySelector(".error-feedback");

  // Get the image element - these are kept as img elements since they're part of the feedback UI
  const feedbackImage = feedbackElement.querySelector("img");

  // Show the feedback container
  feedbackContainer.style.visibility = "visible";
  feedbackContainer.style.opacity = "1";
  feedbackContainer.style.zIndex = "100";

  // Show the feedback element with animation
  feedbackElement.style.visibility = "visible";
  feedbackElement.style.opacity = "1";
  feedbackElement.style.zIndex = "100";

  // Apply animation
  if (feedbackImage) {
    feedbackImage.style.animation = "feedbackAppear 0.5s forwards";
    feedbackImage.style.transform = "scale(1)";
  }

  // Announce for screen readers
  const basketType =
    basket.getAttribute("data-basket") === "left"
      ? "Foods to enjoy in moderation"
      : "Foods to avoid";

  const message = isSuccess
    ? `Success! Correct food placement in the ${basketType} basket.`
    : `Error! Incorrect food placement in the ${basketType} basket.`;

  announceToScreenReader(message);

  // Hide the feedback after 2 seconds
  setTimeout(() => {
    // Reset animation
    if (feedbackImage) {
      feedbackImage.style.animation = "";
    }

    // Hide with smooth transition
    feedbackContainer.style.visibility = "hidden";
    feedbackContainer.style.opacity = "0";
    feedbackContainer.style.zIndex = "-100";

    feedbackElement.style.visibility = "hidden";
    feedbackElement.style.opacity = "0";
    feedbackElement.style.zIndex = "-50";
    feedbackElement.style.transform = "scale(1)";

    if (feedbackImage) {
      feedbackImage.style.transform = "scale(1)";
    }
  }, 2000);
}

// Function to check if all items are dropped
function checkAllItemsDropped(foodItems, playAgainBtn) {
  if (droppedItems === foodItems.length) {
    setTimeout(() => {
      playAgainBtn.style.display = "block";
      playAgainBtn.classList.add("show");
    }, 1000);
  }
}

// Function to handle successful drop
function handleSuccessfulDrop(foodItem, basket) {
  // Mark the item as dropped
  foodItem.classList.add("dropped");
  foodItem.setAttribute("aria-grabbed", "false");
  foodItem.setAttribute("aria-dropeffect", "executed");

  // Update info section with the dropped food's data
  const foodType = foodItem.getAttribute("data-food");
  updateInfoSection(foodType);

  // Create a clone of the SVG component for the basket
  const svgElement = foodItem.querySelector("svg");

  // Create a new image to represent the dropped item
  const miniImage = document.createElement("img");

  // If SVG exists, convert it to a data URL
  if (svgElement) {
    const svgContent = svgElement.outerHTML;
    const dataUrl =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgContent);
    miniImage.src = dataUrl;
  } else {
    // Fallback for component icons
    miniImage.src = `https://labs.theguardian.com/2025/Astra+Zeneca/${foodType}.svg`;
  }

  miniImage.alt = `${foodType} icon`;
  miniImage.width = 30; // Make the cloned image smaller
  miniImage.dataset.food = foodType;

  // Add it to the basket's dropped items container
  basket.querySelector(".dropped-items").appendChild(miniImage);

  // Show success message
  showFeedback(true, basket);

  // Increment dropped items count
  droppedItems++;

  // Check if all items are dropped
  const foodItems = document.querySelectorAll(".food-item");
  const playAgainBtn = document.getElementById("play-again");
  checkAllItemsDropped(foodItems, playAgainBtn);
}

// Function to announce messages to screen readers
function announceToScreenReader(message) {
  const announcer = document.createElement("div");
  announcer.setAttribute("aria-live", "assertive");
  announcer.setAttribute("class", "sr-only");
  announcer.textContent = message;
  document.body.appendChild(announcer);

  setTimeout(() => {
    announcer.remove();
  }, 3000);
}

// Scroll-based demonstration functionality
function setupDemonstration() {
  // Reference to the basket container and food items for demonstration
  const basketsContainer = document.querySelector(".baskets-container");
  const appleItem = document.querySelector('[data-food="apple"]');
  const carrotItem = document.querySelector('[data-food="carrot"]');
  const leftBasket = document.getElementById("left-basket");
  const rightBasket = document.getElementById("right-basket");

  // Function to check if element is in viewport
  function isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;

    // Check if the element's center is in the middle area of the viewport
    const elementCenter = rect.top + rect.height / 2;
    const viewportMiddleStart = windowHeight * 0.4;
    const viewportMiddleEnd = windowHeight * 0.6;

    return (
      elementCenter >= viewportMiddleStart && elementCenter <= viewportMiddleEnd
    );
  }

  // Create visual clone of an item for animation
  function createDraggingClone(item) {
    // Create a clean container for the clone
    const clone = document.createElement("div");
    clone.style.position = "fixed";
    clone.style.zIndex = "1000";
    clone.style.pointerEvents = "none";
    clone.style.width = "80px";
    clone.style.height = "80px";
    clone.style.borderRadius = "50%";
    clone.style.background = "white";
    clone.style.display = "flex";
    clone.style.alignItems = "center";
    clone.style.justifyContent = "center";
    clone.style.boxShadow = "0 5px 15px rgba(0, 0, 0, 0.2)";
    clone.classList.add("demo-clone");

    // Find the SVG in the original item and clone it
    const svgElement = item.querySelector("svg");
    if (svgElement) {
      const svgClone = svgElement.cloneNode(true);
      svgClone.style.width = "70px";
      svgClone.style.height = "70px";
      svgClone.style.maxWidth = "70px";
      svgClone.style.maxHeight = "70px";
      clone.appendChild(svgClone);
    }

    document.body.appendChild(clone);
    return clone;
  }

  // Animation to move element from start to destination
  function animateElement(
    element,
    startX,
    startY,
    destX,
    destY,
    duration,
    onComplete
  ) {
    element.style.left = startX + "px";
    element.style.top = startY + "px";

    const startTime = performance.now();

    function step(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-in-out animation
      const easeProgress =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const currentX = startX + (destX - startX) * easeProgress;
      const currentY = startY + (destY - startY) * easeProgress;

      element.style.left = currentX + "px";
      element.style.top = currentY + "px";

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        if (onComplete) onComplete();
      }
    }

    requestAnimationFrame(step);
  }

  // Simulate the drop and show feedback
  function simulateDrop(item, basket, isSuccessful) {
    if (isSuccessful) {
      // Create clone for the basket
      const foodType = item.getAttribute("data-food");
      const svgElement = item.querySelector("svg");

      // Create a miniature representation of the item
      const miniImage = document.createElement("img");

      // If SVG exists, convert it to a data URL
      if (svgElement) {
        const svgContent = svgElement.outerHTML;
        const dataUrl =
          "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgContent);
        miniImage.src = dataUrl;
      } else {
        // Fallback
        miniImage.src = `https://labs.theguardian.com/2025/Astra+Zeneca/${foodType}.svg`;
      }

      miniImage.alt = `${foodType} icon`;
      miniImage.width = 30;
      miniImage.dataset.food = foodType;
      miniImage.classList.add("demo-mini-clone");

      // Add to basket
      basket.querySelector(".dropped-items").appendChild(miniImage);

      // Mark original as dropped temporarily
      item.classList.add("temp-dropped");
    }

    // Show appropriate feedback
    showFeedback(isSuccessful, basket);
  }

  // Demo steps for successful and unsuccessful drops
  function runDemonstration() {
    if (demonstrationPlayed) return;
    demonstrationPlayed = true;

    // Disable regular drag and drop during demonstration
    const foodItems = document.querySelectorAll(".food-item");
    foodItems.forEach((item) => {
      item.setAttribute("draggable", "false");
    });

    // Step 1: Successful apple drop to left basket
    setTimeout(() => {
      const appleRect = appleItem.getBoundingClientRect();
      const leftBasketRect = leftBasket.getBoundingClientRect();

      const clone = createDraggingClone(appleItem);

      // Start position (center of apple)
      const startX =
        appleRect.left + appleRect.width / 2 - clone.offsetWidth / 2;
      const startY =
        appleRect.top + appleRect.height / 2 - clone.offsetHeight / 2;

      // Destination (center of left basket)
      const destX =
        leftBasketRect.left + leftBasketRect.width / 2 - clone.offsetWidth / 2;
      const destY =
        leftBasketRect.top + leftBasketRect.height / 2 - clone.offsetHeight / 2;

      // Highlight the basket
      leftBasket.classList.add("highlight");

      // Animate the apple to the left basket - faster animation (1000ms instead of 1500ms)
      animateElement(clone, startX, startY, destX, destY, 1000, () => {
        // Remove clone and highlight
        clone.remove();
        leftBasket.classList.remove("highlight");

        // Show success feedback
        simulateDrop(appleItem, leftBasket, true);

        // Step 2: Unsuccessful carrot drop to right basket (wrong basket)
        setTimeout(() => {
          const carrotRect = carrotItem.getBoundingClientRect();
          const rightBasketRect = rightBasket.getBoundingClientRect();

          const carrotClone = createDraggingClone(carrotItem);

          // Start position (center of carrot)
          const startX =
            carrotRect.left +
            carrotRect.width / 2 -
            carrotClone.offsetWidth / 2;
          const startY =
            carrotRect.top +
            carrotRect.height / 2 -
            carrotClone.offsetHeight / 2;

          // Destination (center of right basket)
          const destX =
            rightBasketRect.left +
            rightBasketRect.width / 2 -
            carrotClone.offsetWidth / 2;
          const destY =
            rightBasketRect.top +
            rightBasketRect.height / 2 -
            carrotClone.offsetHeight / 2;

          // Highlight the basket
          rightBasket.classList.add("highlight");

          // Animate the carrot to the right basket - faster animation (1000ms instead of 1500ms)
          animateElement(
            carrotClone,
            startX,
            startY,
            destX,
            destY,
            1000,
            () => {
              // Remove clone and highlight
              carrotClone.remove();
              rightBasket.classList.remove("highlight");

              // Show success feedback instead of error
              simulateDrop(carrotItem, rightBasket, true);

              // Reset the demo and re-enable dragging
              setTimeout(() => {
                resetDemonstration();
              }, 1500);
            }
          );
        }, 1500); // Shorter delay before starting the error demonstration
      });
    }, 800); // Shorter initial delay after scrolling into view (800ms instead of 1000ms)
  }

  // Reset demonstration effects
  function resetDemonstration() {
    // Remove temporary classes and clones
    document.querySelectorAll(".temp-dropped").forEach((item) => {
      item.classList.remove("temp-dropped");
    });

    document.querySelectorAll(".demo-mini-clone").forEach((clone) => {
      clone.remove();
    });

    // Re-enable dragging
    const foodItems = document.querySelectorAll(".food-item");
    foodItems.forEach((item) => {
      item.setAttribute("draggable", "true");

      // Add bouncy animation to all food images with random delays
      const svg = item.querySelector("svg");
      if (svg && !item.classList.contains("dropped")) {
        svg.classList.add("bounce-animation");
        // Add random delay between 0-1.5s for each item
        const randomDelay = Math.random() * 1.5;
        svg.style.animationDelay = `${randomDelay}s`;
      }
    });
  }

  // Listen for scroll events to trigger the demonstration
  let scrollHandler = () => {
    if (!demonstrationPlayed && isElementInViewport(basketsContainer)) {
      runDemonstration();

      // Remove the scroll listener after triggering once
      window.removeEventListener("scroll", scrollHandler);
    }
  };

  window.addEventListener("scroll", scrollHandler);

  // Check if already in viewport on page load
  setTimeout(() => {
    scrollHandler();
  }, 500);
}

// Main initialization code
onMounted(() => {
  const foodItems = document.querySelectorAll(".food-item");
  const baskets = document.querySelectorAll(".basket");
  const playAgainBtn = document.getElementById("play-again");

  // Initialize info section with apple data
  updateInfoSection("apple");

  // Check if it's a touch device
  window.addEventListener("touchstart", function () {
    document.body.classList.add("touch-device");
  });

  // Apply SVG styles to all food items
  foodItems.forEach((item) => {
    const svg = item.querySelector("svg");
    if (svg) {
      svg.style.transition = "all 0.3s ease";
      svg.style.maxWidth = "100%";
      svg.style.maxHeight = "100%";
      svg.style.width = "100px";
    }
  });

  // Setup drag and drop for desktop
  foodItems.forEach((item) => {
    // Set initial ARIA attributes
    item.setAttribute("aria-grabbed", "false");
    item.setAttribute("aria-dropeffect", "none");

    // Add hover event to update info section
    item.addEventListener("mouseenter", () => {
      if (!item.classList.contains("dropped")) {
        const foodType = item.getAttribute("data-food");
        updateInfoSection(foodType);
      }
    });

    // Drag start
    item.addEventListener("dragstart", (e) => {
      if (item.classList.contains("dropped")) {
        e.preventDefault();
        return;
      }

      draggedItem = item;

      // Update info section with the dragged item
      const foodType = item.getAttribute("data-food");
      updateInfoSection(foodType);

      // Create a custom drag image
      if (e.dataTransfer) {
        try {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", item.getAttribute("data-food"));

          // Get the SVG element to create a custom drag image
          const svgElement = item.querySelector("svg");

          if (svgElement) {
            // Create a clone of the SVG for the drag image
            const dragImageContainer = document.createElement("div");
            dragImageContainer.style.position = "absolute";
            dragImageContainer.style.top = "-1000px"; // Hide it initially
            dragImageContainer.style.width = "80px";
            dragImageContainer.style.height = "80px";
            dragImageContainer.style.background = "white";
            dragImageContainer.style.borderRadius = "50%";
            dragImageContainer.style.display = "flex";
            dragImageContainer.style.alignItems = "center";
            dragImageContainer.style.justifyContent = "center";

            // Clone the SVG and add it to the drag image container
            const svgClone = svgElement.cloneNode(true);
            svgClone.style.width = "70px";
            svgClone.style.height = "70px";
            dragImageContainer.appendChild(svgClone);

            // Add shadow to match the demo clone
            dragImageContainer.style.boxShadow =
              "0 4px 15px rgba(0, 0, 0, 0.3)";

            // Add to document temporarily
            document.body.appendChild(dragImageContainer);

            // Set as drag image
            e.dataTransfer.setDragImage(dragImageContainer, 40, 40);

            // Remove the temporary element after a delay
            setTimeout(() => {
              document.body.removeChild(dragImageContainer);
            }, 100);
          }
        } catch (err) {
          console.log("DataTransfer error:", err);
        }
      }

      // ARIA updates
      item.setAttribute("aria-grabbed", "true");

      setTimeout(() => {
        item.classList.add("dragging");
      }, 0);
    });

    // Drag end
    item.addEventListener("dragend", () => {
      draggedItem.classList.remove("dragging");
      draggedItem.setAttribute("aria-grabbed", "false");
      draggedItem = null;
    });

    // Keyboard support for accessibility
    item.addEventListener("keydown", (e) => {
      if (item.classList.contains("dropped")) return;

      // Space or Enter to start dragging
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        draggedItem = item;
        item.classList.add("keyboard-dragging");
        item.setAttribute("aria-grabbed", "true");

        // Announce that user is dragging the item
        announceToScreenReader(
          `Dragging ${
            item.getAttribute("aria-label").split("-")[0]
          }. Use arrow keys to move between baskets and Enter to drop.`
        );
      }

      // Arrow keys to navigate to baskets when item is being dragged
      if (
        item.classList.contains("keyboard-dragging") &&
        (e.key === "ArrowLeft" || e.key === "ArrowRight")
      ) {
        e.preventDefault();

        // Focus the appropriate basket
        const basketToFocus =
          e.key === "ArrowLeft"
            ? document.getElementById("left-basket")
            : document.getElementById("right-basket");

        basketToFocus.focus();
      }
    });
  });

  baskets.forEach((basket) => {
    // Dragover - prevent default to allow drop
    basket.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    });

    // Dragenter - add highlight
    basket.addEventListener("dragenter", (e) => {
      e.preventDefault();
      basket.classList.add("highlight");
    });

    // Dragleave - remove highlight
    basket.addEventListener("dragleave", () => {
      basket.classList.remove("highlight");
    });

    // Drop - handle the drop
    basket.addEventListener("drop", (e) => {
      e.preventDefault();
      basket.classList.remove("highlight");

      if (draggedItem) {
        const foodType = draggedItem.getAttribute("data-type");
        const basketType = basket.getAttribute("data-basket");

        // Check if the item is dropped in the correct basket
        if (foodType === basketType) {
          // Successful drop
          handleSuccessfulDrop(draggedItem, basket);
        } else {
          // Error - wrong basket
          showFeedback(false, basket);
        }
      }
    });

    // Keyboard support for dropping
    basket.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        // Check if there's a dragged item
        const activeElement = document.querySelector(".keyboard-dragging");
        if (activeElement) {
          e.preventDefault();

          // Process the drop
          const foodType = activeElement.getAttribute("data-type");
          const basketType = basket.getAttribute("data-basket");

          // Remove keyboard dragging class
          activeElement.classList.remove("keyboard-dragging");

          // Check if the item is dropped in the correct basket
          if (foodType === basketType) {
            // Successful drop
            draggedItem = activeElement;
            handleSuccessfulDrop(draggedItem, basket);
            draggedItem = null;
          } else {
            // Error - wrong basket
            showFeedback(false, basket);

            // Reset aria-grabbed
            activeElement.setAttribute("aria-grabbed", "false");
          }

          // Return focus to the dragged item
          activeElement.focus();
        }
      }
    });
  });

  // Touch support for mobile devices
  if ("ontouchstart" in window) {
    foodItems.forEach((item) => {
      // Touch start
      item.addEventListener("touchstart", () => {
        if (item.classList.contains("dropped")) return;

        draggedItem = item;
        item.classList.add("dragging");

        // Update info section with the touched item
        const foodType = item.getAttribute("data-food");
        updateInfoSection(foodType);

        // Add active state visual feedback for touch
        item.classList.add("touch-active");
      });

      // Touch move
      item.addEventListener("touchmove", (e) => {
        if (!draggedItem) return;

        e.preventDefault(); // Prevent scrolling while dragging

        const touch = e.touches[0];
        const x = touch.clientX;
        const y = touch.clientY;

        // Move the item with the finger
        // Instead of moving the whole item, create a temporary drag helper if not already exists
        let dragHelper = document.querySelector(".touch-drag-helper");

        if (!dragHelper) {
          dragHelper = document.createElement("div");
          dragHelper.className = "touch-drag-helper";
          dragHelper.style.position = "fixed";
          dragHelper.style.zIndex = "9999";
          dragHelper.style.width = "80px";
          dragHelper.style.height = "80px";
          dragHelper.style.borderRadius = "50%";
          dragHelper.style.background = "white";
          dragHelper.style.boxShadow = "0 5px 15px rgba(0, 0, 0, 0.2)";
          dragHelper.style.display = "flex";
          dragHelper.style.alignItems = "center";
          dragHelper.style.justifyContent = "center";
          dragHelper.style.pointerEvents = "none"; // Allow events to pass through

          // Clone the SVG from the original item
          const svgElement = item.querySelector("svg");
          if (svgElement) {
            const svgClone = svgElement.cloneNode(true);
            svgClone.style.width = "70px";
            svgClone.style.height = "70px";
            dragHelper.appendChild(svgClone);
          }

          // Add shadow to match the demo clone styling
          dragHelper.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.3)";

          document.body.appendChild(dragHelper);
        }

        // Position the helper under the finger
        dragHelper.style.left = x - 40 + "px";
        dragHelper.style.top = y - 40 + "px";

        // Make the original slightly transparent
        item.style.opacity = "0.6";

        // Visual feedback for potential drop targets
        baskets.forEach((basket) => {
          const rect = basket.getBoundingClientRect();

          if (
            x >= rect.left &&
            x <= rect.right &&
            y >= rect.top &&
            y <= rect.bottom
          ) {
            basket.classList.add("highlight");
          } else {
            basket.classList.remove("highlight");
          }
        });
      });

      // Touch end
      item.addEventListener("touchend", (e) => {
        if (!draggedItem) return;

        // Remove touch active state
        item.classList.remove("touch-active");

        // Remove the drag helper
        const dragHelper = document.querySelector(".touch-drag-helper");
        if (dragHelper) {
          document.body.removeChild(dragHelper);
        }

        // Reset the original item
        item.style.opacity = "";
        item.style.transform = "";
        item.classList.remove("dragging");

        const touch = e.changedTouches[0];
        const x = touch.clientX;
        const y = touch.clientY;

        // Check if the item is dropped on a basket
        baskets.forEach((basket) => {
          basket.classList.remove("highlight");
          const rect = basket.getBoundingClientRect();

          if (
            x >= rect.left &&
            x <= rect.right &&
            y >= rect.top &&
            y <= rect.bottom
          ) {
            const foodType = draggedItem.getAttribute("data-type");
            const basketType = basket.getAttribute("data-basket");

            // Check if the item is dropped in the correct basket
            if (foodType === basketType) {
              // Successful drop
              handleSuccessfulDrop(draggedItem, basket);
            } else {
              // Error - wrong basket
              showFeedback(false, basket);
            }
          }
        });

        draggedItem = null;
      });
    });
  }

  // Play Again button
  playAgainBtn.addEventListener("click", () => {
    // Reset the game
    droppedItems = 0;

    // Remove all dropped items from baskets
    document.querySelectorAll(".dropped-items").forEach((container) => {
      container.innerHTML = "";
    });

    // Reset food items
    foodItems.forEach((item) => {
      item.classList.remove("dropped");
      item.setAttribute("aria-grabbed", "false");
      item.setAttribute("aria-dropeffect", "none");
    });

    // Reset info section to apple
    updateInfoSection("apple");

    // Hide the play again button
    playAgainBtn.style.display = "none";
    playAgainBtn.classList.remove("show");

    // Announce game restart
    announceToScreenReader(
      "Game restarted. Place the foods in the correct baskets."
    );
  });

  // Add animation to the food items
  foodItems.forEach((item) => {
    item.addEventListener("mouseenter", () => {
      if (!item.classList.contains("dropped")) {
        const svg = item.querySelector("svg");
        if (svg) {
          svg.style.transform = "scale(1.1)";
        }
      }
    });

    item.addEventListener("mouseleave", () => {
      const svg = item.querySelector("svg");
      if (svg) {
        svg.style.transform = "scale(1)";
      }
    });
  });

  // Add CSS for touch and keyboard dragging
  const additionalStyles = document.createElement("style");
  additionalStyles.textContent = `
    .touch-active {
      opacity: 0.8;
    }
    .keyboard-dragging {
      outline: 3px dashed #4a90e2;
      background-color: rgba(74, 144, 226, 0.1);
    }
    /* Touch dragging helper */
    .touch-drag-helper {
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
      z-index: 9999;
      border-radius: 50%;
      background: white;
      width: 80px;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: fixed;
      pointer-events: none;
      cursor: grabbing;
    }
    .touch-drag-helper svg {
      width: 70px;
      height: 70px;
    }
    .temp-dropped {
      opacity: 0.7;
      filter: brightness(0) saturate(100%) invert(87%) sepia(10%) saturate(431%) hue-rotate(175deg) brightness(92%) contrast(91%);
      pointer-events: none;
    }
    .bounce-animation {
      animation: bounce 1.5s ease infinite;
    }
    @keyframes bounce {
      0%, 100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-10px);
      }
    }
  `;
  document.head.appendChild(additionalStyles);

  // Initialize the demonstration
  setupDemonstration();

  // Initial announcement for screen readers
  announceToScreenReader(
    "Drag and drop game loaded. Place foods in the correct baskets. Foods on the left go to the 'Foods to enjoy in moderation' basket. Foods on the right go to the 'Foods to avoid' basket."
  );
});
</script>

<style>
.potasium-drag__container {
  padding-top: 60px;
}
.az-drag-drop__container {
  padding: 20px;
  border-radius: 20px;
  background-color: #e3edfd;
  box-shadow: 0 20px 0 #027dbb;
}

/* Baskets Styles */
.baskets-container {
  display: grid;
  justify-content: space-between;
  margin-bottom: 30px;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.basket-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  border-radius: 50%;
  width: 430px;
  height: 430px;
  justify-content: center;
  position: relative;
}

.feedback-container__basket {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: -100;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.4s ease, visibility 0.4s ease, z-index 0s 0.4s;
  pointer-events: none;
}

.feedback-container__basket-item img {
  width: 100%;
  height: 100%;
  transform: scale(1);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.feedback-container__basket-item {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: -50;
  opacity: 0;
  visibility: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform: scale(1);
}

.basket {
  background-color: white;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
}

.basket:hover {
  transform: translateY(-5px);
}

.basket.highlight {
  transform: scale(1.08);

  border: 3px dashed #4a90e2;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(74, 144, 226, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(74, 144, 226, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(74, 144, 226, 0);
  }
}

.basket {
  font-size: 50px;

  transition: transform 0.3s ease;
  background-image: url(https://labs.theguardian.com/2025/Astra+Zeneca/basket.svg);
  background-size: 90%;
  background-position: center;
  background-repeat: no-repeat;
}

.basket:hover i.fa-basket-shopping {
  transform: scale(1.1);
}

.basket-label {
  text-align: left;
  margin-top: -20px;
  font-weight: bold;
  color: #1a3a5a;
  font-size: 25px;
  width: 12ch;
  padding-left: 1.3rem;
  position: relative;
}

.dropped-items {
  display: flex;

  justify-content: center;
  gap: 8px;
  margin-top: 120px;
  min-height: 30px;
}

.dropped-items img {
  max-width: 60px;
  max-height: 60px;
  width: 100%;
  height: 100%;
  animation: popIn 0.4s ease-out;
  filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.2));
}

@keyframes popIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  60% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Food Items Styles */
.foods-container {
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.foods-row {
  display: flex;
  justify-content: center;
  flex-direction: column;
  justify-content: center;

  position: relative;
}
.foods-row .food-item::after {
  content: "";
  width: 100%;
  height: 5px;
  display: inline-block;
  position: absolute;
  background-color: #824096;

  bottom: 0;
}
.foods-row .food-item[data-position="left"]::after {
  border-bottom-left-radius: 20px;
  border-top-left-radius: 20px;
}
.foods-row .food-item[data-position="right"]::after {
  border-bottom-right-radius: 20px;
  border-top-right-radius: 20px;
}

.food-item {
  width: 120px;
  height: 140px;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: grab;
  position: relative;
  transition: transform 0.2s ease;
  background-color: rgba(255, 255, 255, 0.05);
  -webkit-user-select: none;
  user-select: none;
}

.food-item:active {
  cursor: grabbing;
}

.food-item svg {
  max-width: 100%;
  max-height: 100%;
  transition: all 0.3s ease;
  filter: drop-shadow(2px 2px 3px rgba(0, 0, 0, 0.2));
  width: 100px;
}

/* Make just the SVG appear to be dragged */
.food-item.dragging {
  z-index: 10;
  border-bottom: 0 !important;
}

/* Show placeholder of original item in a faded state */
.food-item.dragging svg {
  opacity: 0.5;
}

.food-item.dropped svg {
  pointer-events: none;
  animation: fadeOut 0.5s forwards;
}

@keyframes fadeOut {
  to {
    opacity: 0.7;
    /* This filter creates the #ADC1D5 color effect on SVGs */
    filter: brightness(0) saturate(100%) invert(87%) sepia(10%) saturate(431%)
      hue-rotate(175deg) brightness(92%) contrast(91%);
  }
}

.food-item.dropped svg {
  opacity: 0.7;
  filter: brightness(0) saturate(100%) invert(87%) sepia(10%) saturate(431%)
    hue-rotate(175deg) brightness(92%) contrast(91%);
  pointer-events: none;
}

/* Food item specific colors - these are no longer needed with images */
.food-item[data-food="apple"] svg,
.food-item[data-food="banana"] svg,
.food-item[data-food="carrot"] svg,
.food-item[data-food="coffee"] svg,
.food-item[data-food="beans"] svg,
.food-item[data-food="chocolate"] svg {
  /* No color needed - using actual SVG components */
}

/* Play Again button */
.play-again-btn {
  display: none;
  margin: 20px auto;
  padding: 12px 25px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.play-again-btn:hover {
  background-color: #357abd;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.play-again-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.play-again-btn.show {
  display: block;
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Info section */
.info-section {
  margin-top: 30px;
  text-align: center;
  padding: 15px;
}

.info-section h2 {
  margin-bottom: 10px;
  color: #1a3a5a;
  font-size: 1.3rem;
}

.info-section p {
  font-family: "Guardian Sans Web";
  font-weight: 400;
  line-height: 1.2;
  font-size: 24px;
  padding-top: 10px;
  max-width: 60ch;
  justify-self: center;
  margin: auto;
  min-height: 100px;
}
@media (max-width: 1230px) {
  .basket-wrapper {
    width: 350px;
    height: 350px;
  }
  .basket {
    background-color: white;
    width: 200px;
    height: 200px;
  }
  .basket-label {
    font-size: 18px;

    padding-left: 1rem;
  }
  .dropped-items {
    margin-top: 85px;
  }
  .food-item {
    width: 100px;
    height: 100px;
  }
}
@media (max-width: 1020px) {
  .baskets-container {
    grid-template-columns: 1fr 1fr;
  }
  .foods-row .food-item[data-position="left"]:nth-child(2):after {
    border-radius: 0;
  }
  .foods-row .food-item[data-position="left"]:nth-child(3):after {
    border-radius: 0;
    border-top-right-radius: 50px;
    border-bottom-right-radius: 50px;
  }
  .foods-row .food-item[data-position="right"]:nth-child(1):after {
    border-radius: 0;
    border-top-left-radius: 50px;
    border-bottom-left-radius: 50px;
  }
  .foods-row .food-item[data-position="right"]:nth-child(2):after {
    border-radius: 0;
  }
  .foods-row .food-item[data-position="right"]:nth-child(3):after {
    border-radius: 0;
    border-top-right-radius: 50px;
    border-bottom-right-radius: 50px;
  }
  .basket-wrapper:nth-of-type(1) {
    grid-column-start: 1;
    grid-column-end: 1;
    justify-self: center;
  }
  .basket-wrapper:last-of-type {
    grid-column-start: 2;
    grid-column-end: 2;
    justify-self: center;
  }
  .foods-container {
    grid-row-start: 2;
    grid-row-end: 2;
    grid-column-start: 1;
    grid-column-end: 3;
  }
  .foods-container {
    flex-direction: column;
  }
  .foods-row {
    flex-direction: row;
  }
}
/* Responsive Styles */
@media (max-width: 768px) {
  .az-drag-drop__container {
    margin: 10px;
    width: calc(100% - 20px);
  }

  h1 {
    font-size: 1.5rem;
    margin-bottom: 20px;
  }

  .basket-wrapper {
    width: 250px;
    height: 250px;
    margin: 0 auto;
  }

  .basket {
    width: 180px;
    height: 180px;
  }

  .foods-container {
    margin: 20px auto;
    width: 100%;
  }
  .dropped-items img {
    max-width: 20px;
    max-height: 20px;
  }
  .dropped-items {
    margin-top: 50px;
  }

  .basket-label {
    font-size: 0.9rem;
  }
}
@media (max-width: 600px) {
  .basket-wrapper {
    width: 200px;
    height: 200px;
  }
  div#left-basket {
    margin-top: 16px !important;
  }
  .basket {
    background-size: 75%;
  }
  .basket-label {
    margin-top: 0px;
    padding-bottom: 20px;
  }
}
/* For very small screens */
@media (max-width: 480px) {
  div#left-basket {
    margin-top: 19px !important;
  }
  .basket {
    background-size: 90%;
  }
  .az-drag-drop__container {
    padding: 10px;
  }

  .basket-wrapper {
    width: 176px;
    height: 176px;
  }

  .basket {
    width: 100px;
    height: 100px;
  }
  .foods-row {
    width: 100%;
  }
  .food-item svg {
    width: 70px;
    height: auto;
  }
  .basket-label {
    font-family: "Guardian Text Sans Web";
    font-weight: 800;
    font-size: 15.17px;
    margin-top: 0;
  }
  .foods-container {
    margin-top: 30px;
  }
  .dropped-items img {
    max-width: 25px;
    max-height: 25px;
  }
}

/* Accessibility improvements */
.food-item:focus,
.basket:focus {
  outline: none;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Feedback animation keyframes */
@keyframes feedbackAppear {
  0% {
    opacity: 0;
    transform: scale(0.7);
  }
  70% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Demonstration styles */
.demo-clone {
  cursor: grabbing;
  transition: transform 0.2s ease;
  border-radius: 50%;
  overflow: hidden;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.demo-mini-clone {
  animation: popIn 0.4s ease-out;
}

.temp-dropped {
  opacity: 0.7;
  filter: brightness(0) saturate(100%) invert(87%) sepia(10%) saturate(431%)
    hue-rotate(175deg) brightness(92%) contrast(91%);
  pointer-events: none;
}
</style>
